/**
 * Fushë ore — e shkruar me dorë, sepse `<input type="time">` s'ishte e jona.
 *
 * Zgjedhësi i shfletuesit është dy kolona numrash që rrëshqasin, del jashtë
 * dritares mbi të cilën ndodhet, dhe nuk njeh asgjë nga puna: të ofron 11:37 me
 * të njëjtin peshë si 11:30. Turnet dhe pauzat bien mbi çerekë ore, ndaj lista
 * këtu shkon me hap (`step`) dhe ora e çuditshme mbetet e mundshme — por duhet
 * shkruar, jo zgjedhur.
 *
 * ── Tri gjëra që i mbajnë të dhënat të pacenuara ────────────────────────────
 *  1. **Shkrimi nuk e prish vlerën.** Teksti i ndërmjetëm («9», «93») rri te
 *     gjendja e brendshme; vlera e vërtetë ndryshon vetëm kur teksti është orë
 *     e plotë. Një fushë e zbrazur gjatë shkrimit do t'i fshinte orët e turnit
 *     te forma që e mban.
 *  2. **Ç'nuk lexohet dot, kthehet.** Te `blur` teksti i pakuptueshëm zëvendësohet
 *     nga vlera e fundit e vlefshme — jo nga bosh, sepse bosh është vendim.
 *  3. **Bosh mbetet bosh.** Fshirja e tërë tekstit është mënyra për të thënë
 *     «pa orë», dhe ajo kalon.
 *
 * `relativeTo` e bën listën të flasë gjuhën e formës: te «Deri», çdo orë shkruan
 * sa zgjat nga fillimi («12:30 · 30 min»), që kohëzgjatja të mos llogaritet me
 * mend para se të zgjidhet.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TextField, Menu, MenuItem, Box, Typography, InputAdornment, IconButton } from '@mui/material';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** Minuta nga «HH:MM», ose null. */
export const timeToMin = (t) => {
  const m = HHMM.exec(String(t || ''));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
export const minToTime = (min) => {
  const m = ((min % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

/**
 * Lexon atë që shkruan njeriu, jo atë që pret makina: «9», «930», «9:3», «0930».
 * Kthen «HH:MM», ose null nëse ende s'është orë.
 */
export function parseTime(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  const digits = s.replace(/\D/g, '');
  // Pa asnjë shifër s'ka orë. Pa këtë, «abc» binte te dega e parë me `Number('')`
  // → 0, dhe fusha shkruante 00:00 — një orë e trilluar nga një gabim shtypi.
  if (!digits) return null;
  const colon = s.includes(':');
  let h, m;
  if (colon) {
    const [a, b] = s.split(':');
    h = Number(a); m = b === '' ? 0 : Number(b);
  } else if (digits.length <= 2) {          // «9» → 09:00
    h = Number(digits); m = 0;
  } else if (digits.length === 3) {         // «930» → 09:30
    h = Number(digits.slice(0, 1)); m = Number(digits.slice(1));
  } else {                                   // «0930» / «1230»
    h = Number(digits.slice(0, 2)); m = Number(digits.slice(2, 4));
  }
  if (!Number.isFinite(h) || !Number.isFinite(m) || h > 23 || m > 59) return null;
  return minToTime(h * 60 + m);
}

/** Sa zgjat nga `from` te `to`; kalimi i mesnatës është turn nate, jo minus. */
const span = (from, to) => {
  const a = timeToMin(from), b = timeToMin(to);
  if (a == null || b == null) return null;
  return b >= a ? b - a : 1440 - a + b;
};
const fmtSpan = (min) => (min >= 60
  ? `${Math.floor(min / 60)}h${min % 60 ? ` ${min % 60}m` : ''}`
  : `${min}m`);

export default function TimeField({
  value = '', onChange, step = 15, relativeTo = null,
  disabled = false, sx, inputProps, placeholder = '--:--',
}) {
  const [text, setText] = useState(value || '');
  const [anchor, setAnchor] = useState(null);
  const focused = useRef(false);
  const selRef = useRef(null);

  // Ndryshimet nga jashtë hyjnë vetëm kur fusha s'është nën gisht — përndryshe
  // një rirender do t'i rishkruante shkronjat sapo të shtypura.
  useEffect(() => { if (!focused.current) setText(value || ''); }, [value]);

  const options = useMemo(() => {
    const out = [];
    for (let m = 0; m < 1440; m += step) out.push(minToTime(m));
    return out;
  }, [step]);

  const commit = () => {
    const next = parseTime(text);
    if (next === '') { onChange(''); setText(''); return; }   // bosh = vendim
    if (next === null) { setText(value || ''); return; }      // e palexueshme = kthehu
    setText(next);
    if (next !== value) onChange(next);
  };

  const nudge = (delta) => {
    const base = timeToMin(value) ?? 0;
    const next = minToTime(base + delta * step);
    setText(next); onChange(next);
  };

  const pick = (t) => { setAnchor(null); setText(t); onChange(t); };

  return (
    <>
      <TextField
        size="small" value={text} disabled={disabled} placeholder={placeholder}
        onFocus={() => { focused.current = true; }}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => { focused.current = false; commit(); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter')      { e.preventDefault(); commit(); }
          else if (e.key === 'ArrowUp')   { e.preventDefault(); nudge(1); }
          else if (e.key === 'ArrowDown') { e.preventDefault(); nudge(-1); }
        }}
        inputProps={{ inputMode: 'numeric', 'data-testid': 'time-field', ...(inputProps || {}) }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ ml: 0 }}>
              <IconButton size="small" disabled={disabled} tabIndex={-1}
                aria-label="Zgjidh orën"
                onClick={(e) => setAnchor(e.currentTarget)} sx={{ p: .25 }}>
                <ScheduleRoundedIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={sx}
      />

      <Menu
        anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        // Menyja ka gjerësinë e vet: fusha është 104px, kurse rreshti mban orën
        // dhe kohëzgjatjen. Lartësia kapet, që 96 çereqe të mos mbulojnë faqen.
        MenuListProps={{ dense: true, sx: { py: .5 } }}
        PaperProps={{ sx: { maxHeight: 240, width: relativeTo ? 148 : 96, borderRadius: 2 } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        TransitionProps={{ onEntered: () => selRef.current?.scrollIntoView({ block: 'center' }) }}
      >
        {options.map((t) => {
          // «I zgjedhuri» është ora më e afërt poshtë vlerës, jo vetëm përputhja e
          // saktë: një orë e shkruar me dorë (11:37) përndryshe do ta hapte listën
          // te mesnata, larg vendit ku ndodhet njeriu.
          const cur = timeToMin(value);
          const mine = timeToMin(t);
          const isSel = cur != null && mine <= cur && cur < mine + step;
          const d = relativeTo ? span(relativeTo, t) : null;
          return (
            <MenuItem key={t} selected={isSel} ref={isSel ? selRef : null}
              onClick={() => pick(t)}
              sx={{ py: .35, px: 1, gap: 1, fontSize: '0.8rem' }}>
              <Box className="tabular" sx={{ fontWeight: isSel ? 700 : 500 }}>{t}</Box>
              {d != null && d > 0 && (
                <Typography sx={{ ml: 'auto', fontSize: '0.7rem', color: 'text.secondary' }}>
                  {fmtSpan(d)}
                </Typography>
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
